'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

interface ResizableColumnOptions {
  /** localStorage key holding this column's remembered width. */
  storageKey: string;
  /** Narrowest the column goes — also its width on a laptop-sized window. */
  minWidth: number;
  /** Widest it goes, before the half-the-window rule is applied on top. */
  maxWidth: number;
  /** Width this window picks on its own, before the user drags anything. */
  defaultWidthFor: (windowWidth: number) => number;
  /**
   * Which edge carries the grip. A column on the trailing side (the inspector)
   * grows when its LEADING edge is dragged inward; a leading column (the nav
   * sidebar) grows when its TRAILING edge is dragged outward.
   */
  edge: 'leading' | 'trailing';
}

interface ResizableColumn {
  /** Current width, or null until the stored value has been read. */
  width: number | null;
  /** Attach to the element being resized — the width is measured from it. */
  ref: RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  /** Spread onto the invisible grip element. */
  gripHandlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onDoubleClick: () => void;
  };
}

/**
 * A layout column the user can resize by dragging an edge, with the choice
 * remembered per browser.
 *
 * Shared by the inspector (render settings / estimate / album) and the
 * navigation sidebar so the two behave identically: the same clamp against the
 * window, the same persistence, and the same double-click back to the width the
 * window would have picked. What differs — which edge, and how the grip looks —
 * stays with the caller.
 *
 * Until the stored width is read the hook reports `null`, so the first paint can
 * use plain CSS breakpoints and nothing jumps on hydration.
 */
export function useResizableColumn({
  storageKey,
  minWidth,
  maxWidth,
  defaultWidthFor,
  edge,
}: ResizableColumnOptions): ResizableColumn {
  const [width, setWidth] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // `latest` is what gets persisted: on pointerup React has not necessarily
  // committed the last setWidth yet, so measuring the element there saves the
  // width from one move ago instead of the one the user let go at.
  const dragRef = useRef<{
    startX: number;
    startWidth: number;
    latest: number;
  } | null>(null);

  const clamp = useCallback(
    (value: number, windowWidth: number) => {
      // Never let one column eat more than half the window, however wide it is.
      const upper = Math.max(Math.min(maxWidth, Math.round(windowWidth / 2)), minWidth);
      return Math.min(Math.max(value, minWidth), upper);
    },
    [minWidth, maxWidth]
  );

  // Resolve the starting width once mounted: a stored choice if there is one,
  // otherwise the default for this window. Both go through clamp() so a value
  // saved on a wide monitor cannot leave a narrow window with no content.
  useEffect(() => {
    let stored: number | null = null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(parsed)) stored = parsed;
    } catch {
      // Private mode / blocked storage — fall back to the window default.
    }
    setWidth(
      clamp(stored ?? defaultWidthFor(window.innerWidth), window.innerWidth)
    );
  }, [storageKey, clamp, defaultWidthFor]);

  // A window that shrinks (a smaller display, a split screen) must not leave a
  // stored width covering the content.
  useEffect(() => {
    const onResize = () =>
      setWidth((current) =>
        current === null ? current : clamp(current, window.innerWidth)
      );
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const persist = useCallback(
    (value: number) => {
      try {
        window.localStorage.setItem(storageKey, String(Math.round(value)));
      } catch {
        // Not being able to remember the width is not a reason to refuse resizing.
      }
    },
    [storageKey]
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      const current = ref.current?.getBoundingClientRect().width;
      if (current === undefined) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        startX: event.clientX,
        startWidth: current,
        latest: current,
      };
      setIsDragging(true);
      // The cursor and the text selection belong to the whole document while a
      // drag is in flight, or the pointer leaving the grip reverts both.
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    []
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const isRtl = document.documentElement.dir === 'rtl';
      const towardsLeading = drag.startX - event.clientX;
      // A trailing column grows when its leading edge moves inward; a leading
      // column grows when its trailing edge moves outward. RTL mirrors both.
      const signed = edge === 'leading' ? towardsLeading : -towardsLeading;
      const delta = isRtl ? -signed : signed;
      const next = clamp(drag.startWidth + delta, window.innerWidth);
      drag.latest = next;
      setWidth(next);
    },
    [clamp, edge]
  );

  const endDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const settled = dragRef.current.latest;
      dragRef.current = null;
      setIsDragging(false);
      event.currentTarget.releasePointerCapture(event.pointerId);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      persist(settled);
    },
    [persist]
  );

  const resetWidth = useCallback(() => {
    const next = clamp(defaultWidthFor(window.innerWidth), window.innerWidth);
    setWidth(next);
    persist(next);
  }, [clamp, defaultWidthFor, persist]);

  return {
    width,
    ref,
    isDragging,
    gripHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick: resetWidth,
    },
  };
}
